// UserMenu — the user card at the foot of the navigation panel (task 355.4; the menu moved here from
// the old TopBar). Theme preferences, density, logout.

import React from 'react';
import { Dropdown, Switch } from 'antd';
import type { MenuProps } from 'antd';
import { KeyRound, LogOut, Monitor, Moon, Rows3, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../stores/authStore';
import { useThemeStore } from '../../../stores/themeStore';
import { ROUTES } from '../../utils/constants';
import styles from './Shell.module.css';

const APP_VERSION =
  (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined) ?? '0.0.0';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
}

function SwitchRow({ label, checked }: { label: string; checked: boolean }): React.ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 180,
        gap: 12,
      }}
    >
      <span>{label}</span>
      <Switch size="small" checked={checked} />
    </div>
  );
}

export default function UserMenu({ collapsed }: { collapsed: boolean }): React.ReactElement {
  const { user, roles, signOut } = useAuth();
  const { mode, darkMode, compact, toggleDarkMode, setMode, toggleCompact } = useThemeStore();
  const navigate = useNavigate();

  const displayName = user?.fullName ?? user?.username ?? 'User';
  const role = roles[0];

  const items: MenuProps['items'] = [
    // Task 355.1: default follows the OS; Dark Mode pins a choice, this switch un-pins it.
    {
      key: 'theme-system',
      icon: <Monitor size={15} />,
      label: <SwitchRow label="Match System Theme" checked={mode === 'system'} />,
      onClick: () => setMode(mode === 'system' ? (darkMode ? 'dark' : 'light') : 'system'),
    },
    {
      key: 'theme-dark',
      icon: <Moon size={15} />,
      label: <SwitchRow label="Dark Mode" checked={darkMode} />,
      onClick: toggleDarkMode,
    },
    {
      key: 'theme-compact',
      icon: <Rows3 size={15} />,
      label: <SwitchRow label="Compact Mode" checked={compact} />,
      onClick: toggleCompact,
    },
    { type: 'divider' },
    {
      key: 'change-password',
      icon: <KeyRound size={15} />,
      label: 'Change password',
      onClick: () => navigate('/account/password'),
    },
    {
      key: 'security',
      icon: <ShieldCheck size={15} />,
      label: 'Two-step verification',
      onClick: () => navigate('/account/security'),
    },
    { key: 'version', label: `Console v${APP_VERSION}`, disabled: true },
    {
      key: 'logout',
      icon: <LogOut size={15} />,
      label: 'Logout',
      danger: true,
      // Must reach the server (task 326.18): the refresh token is an httpOnly cookie, and only the
      // host that set it can clear it. A purely client-side logout would leave a session that the
      // next page load silently restores.
      onClick: () => {
        void signOut().then(() => navigate(ROUTES.login, { replace: true }));
      },
    },
  ];

  return (
    <Dropdown menu={{ items }} placement="topLeft" trigger={['click']}>
      <button type="button" className={styles.me} aria-label={`Account menu for ${displayName}`}>
        <span className={styles.avatar} aria-hidden="true">
          {initials(displayName)}
        </span>
        {!collapsed && (
          <span className={styles.meText}>
            <span className={styles.meName}>{displayName}</span>
            {role && <span className={styles.meRole}>{role}</span>}
          </span>
        )}
      </button>
    </Dropdown>
  );
}
